import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import reduceReducers from 'reduce-reducers';

import { type PrintPageScale, type PrintPageViewerData } from '@frontend/api';

import { createEntitiesReducer } from 'modules/common/redux';

import type { RootState } from 'store/configureStore';

import { createDeepMemoizedSelector } from 'utils/reduxUtils';

import { DEFAULT_SCALE } from '../pdfViewer/data/constants';
import type { ViewerPartFile } from '../types';
import { types, type ViewerAction } from './actions';
import { partFileSchema } from './entities';

type Entities = {
  partFiles: Record<string, ViewerPartFile>;
  printPages: Record<string, PrintPageViewerData>;
};

type ViewerState = {
  activePartFileUuid: string | null;
  entities: Entities;
  isFetchingVectorization: boolean;
  isFetchingViewerModel: boolean;
  legacyDxfViewerByPartFileUuid: Record<string, boolean>;
  pageIdToVectorization: Record<string, unknown>;
};

const initialState = (): ViewerState => ({
  activePartFileUuid: null,
  entities: {
    partFiles: {},
    printPages: {},
  },
  isFetchingVectorization: false,
  isFetchingViewerModel: false,
  // per-file override forcing the legacy 2D DXF viewer over the new DXF scene viewer
  legacyDxfViewerByPartFileUuid: {},
  pageIdToVectorization: {},
});

const entitiesReducer = createEntitiesReducer<ViewerState>(
  ['partFiles', 'printPages'],
  [types.RECEIVE_PART_FILE, types.RECEIVE_PART_FILES, types.RECEIVE_PRINT_PAGE],
);
const reducer = (state: ViewerState = initialState(), action: ViewerAction) => {
  switch (action.type) {
    case types.RECEIVE_ACTIVE_PART_FILE_UUID:
      return {
        ...state,
        activePartFileUuid: action.payload.partFileUuid,
      };
    case types.RECEIVE_PAGE_VECTORIZATION:
      return dot.merge(
        state,
        'pageIdToVectorization',
        action.payload.pageIdToVectorization,
      );
    case types.SET_LEGACY_DXF_VIEWER_FOR_PART_FILE:
      return dot.set(
        state,
        `legacyDxfViewerByPartFileUuid.${action.payload.partFileUuid}`,
        action.payload.isLegacyDxfViewer,
      );
    case types.RESET:
      return initialState();
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState());

const getScope = (state: RootState): ViewerState =>
  state.modules.common.viewer.viewer;

export const getPartFileByUuid = (
  state: RootState,
  partFileUuid: string | null,
): ViewerPartFile | undefined =>
  getScope(state).entities.partFiles?.[partFileUuid ?? ''];

export const getPartFilesByUuids = (
  state: RootState,
  partFileUuids: string[],
): ViewerPartFile[] =>
  Object.values(getScope(state).entities.partFiles ?? {}).filter((partFile) =>
    partFileUuids.includes(partFile.uuid),
  );

const getPartFileByUuidDenormalized = (
  state: RootState,
  partFileUuid: string,
) => denormalize(partFileUuid, partFileSchema, getScope(state).entities);

export const getActivePartFileUuid = (state: RootState) =>
  getScope(state).activePartFileUuid;

export const getActiveFile = (state: RootState) =>
  getPartFileByUuid(state, getActivePartFileUuid(state));

export const getIsLegacyDxfViewerForPartFile = (
  state: RootState,
  partFileUuid: string | null | undefined,
) => Boolean(getScope(state).legacyDxfViewerByPartFileUuid[partFileUuid ?? '']);

export const getActivePartPmiUrl = (state: RootState) => {
  const activeFile = getActiveFile(state);
  if (activeFile) {
    return activeFile.pmiUrl;
  }
};

export const getActivePartFilesRootPartFileUuid = (state: RootState) => {
  // gets the root file uuid of the active part file
  // for instance, if the active part file is from an assembly, will return the top part file uuid
  const activeFile = getActiveFile(state);
  if (!activeFile?.hasAssemblyParentFile) {
    return activeFile?.uuid;
  } else {
    return activeFile?.assemblyParentFileUuid;
  }
};

export const getActiveFilesFlatFile = (state: RootState) =>
  getPartFileByUuid(state, getActiveFile(state)?.flatFileUuid ?? null);

export const getActiveFileDenormalized = (state: RootState) =>
  getPartFileByUuidDenormalized(state, getActivePartFileUuid(state) ?? '');

export const getActiveFileProperties = (state: RootState) => {
  const { area, volume, weight } = getActiveFile(state) || {};
  return {
    ...(area && { area }),
    ...(volume && { volume }),
    ...(weight && { weight }),
  };
};

export const getIsActiveFileAssembly = (state: RootState) => {
  const activeFile = getActiveFile(state);
  return (activeFile || {}).isAssembly;
};

export const getDoesActiveFileHaveOptimalBoundingBox = (state: RootState) => {
  const activeFile = getActiveFile(state);
  return (activeFile || {}).hasOptimalBoundingBox;
};

export const getIsFetchingViewerModel = (state: RootState) =>
  getScope(state).isFetchingViewerModel;

export const getPrintPageIdByNumber = (
  state: RootState,
  partFileUuid: string,
  pageNumber: number,
) => {
  const partFile = getPartFileByUuidDenormalized(state, partFileUuid);
  const printPage = (partFile?.printPages ?? []).find(
    (pp: PrintPageViewerData) => pp.pageNumber === pageNumber,
  );
  return (printPage || {}).id;
};

export const getPrintPageScales = createDeepMemoizedSelector(
  (state: RootState) => getActiveFileDenormalized(state)?.printPages ?? [],
  (cachedPrintPages: PrintPageViewerData[]) => {
    const pageScales: Record<number, PrintPageScale> = {};
    cachedPrintPages.forEach((page) => {
      if (page.scaleFromDimension && page.scaleToDimension) {
        pageScales[page.pageNumber] = {
          scaleFromDimension: page.scaleFromDimension,
          scaleFromUnit: page.scaleFromUnit,
          scaleToDimension: page.scaleToDimension,
          scaleToUnit: page.scaleToUnit,
        };
      } else {
        pageScales[page.pageNumber] = DEFAULT_SCALE;
      }
    });
    return pageScales;
  },
);
